import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/hooks/use-language';
import { useAuth } from '@/hooks/use-auth';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useSession } from '@clerk/clerk-react';
import {
  TournamentContextType,
  ExtendedTournamentState,
  defaultTournamentState
} from './types';
import { 
  createStartTournament,
  createSubmitRoundResults,
  createFinishAmericanoRound,
  createGoToPreviousRound
} from './tournamentActions';
import { TournamentContext } from './TournamentContext';
import { addBreadcrumb } from '@/lib/sentry';
import { useRealtimeTournament } from '@/hooks/use-realtime-tournament';

export function TournamentProvider({ children }: { children: React.ReactNode }) {
  const supabase = useSupabase();
  const { session } = useSession();
  console.log('🔍 TournamentProvider supabase client:', !!supabase, 'Type:', typeof supabase);
  const [tournamentState, setTournamentState] = useState<ExtendedTournamentState>(defaultTournamentState);
  const schemaVersion = 2;
  const lastSavedRef = useRef<string>('');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [currentTournamentId, setCurrentTournamentId] = useState<string | null>(null);
  const hasLoaded = useRef(false);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  // Create a token getter function for Edge Function authentication
  const getToken = useCallback(async () => {
    if (!session) return null;
    try {
      return await session.getToken();
    } catch (error) {
      console.warn('[TournamentProvider] Failed to get Clerk token', error);
      return null;
    }
  }, [session]);

  // Real-time tournament updates
  useRealtimeTournament({
    onTournamentUpdate: (payload) => {
      if (payload.new?.user_id === user?.id) {
        const newState = JSON.parse(payload.new.state || '{}');
        setTournamentState(newState);
      }
    },
  });

  const refresh = useCallback(async () => {
    if (!user) return;
    const isUuid = typeof user.id === 'string' && /^[0-9a-fA-F-]{36}$/.test(user.id);
    if (!isUuid) return;
    try {
      let query = supabase
        .from('tournaments_state')
        .select('tournament_id, state')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (currentTournamentId) {
        query = supabase
          .from('tournaments_state')
          .select('tournament_id, state')
          .eq('user_id', user.id)
          .eq('tournament_id', currentTournamentId)
          .maybeSingle();
      }

      const { data, error } = await query;
      if (error) throw error;
      if (data?.state) {
        const incoming = data.state as any;
        // validate and migrate incoming state without external deps
        const migrateState = (s: any): ExtendedTournamentState => {
          const merged: ExtendedTournamentState = {
            ...defaultTournamentState,
            ...s,
            stateVersion: typeof s?.stateVersion === 'number' ? s.stateVersion : schemaVersion,
          };
          // sanitize common fields
          merged.players = Array.isArray(merged.players) ? merged.players : [];
          merged.courts = Array.isArray(merged.courts) ? merged.courts : [];
          merged.results = Array.isArray(merged.results) ? merged.results : [];
          merged.currentRound = Number.isFinite(merged.currentRound) ? merged.currentRound : 0;
          merged.totalRounds = Number.isFinite(merged.totalRounds) ? merged.totalRounds : 0;
          merged.americanoPlayers = Array.isArray(merged.americanoPlayers) ? merged.americanoPlayers : [] as any;
          merged.americanoMatches = Array.isArray(merged.americanoMatches) ? merged.americanoMatches : [] as any;
          merged.americanoRoundResults = Array.isArray(merged.americanoRoundResults) ? merged.americanoRoundResults : [] as any;
          merged.roundCourts = Array.isArray(merged.roundCourts) ? merged.roundCourts : [] as any;
          merged.roundAmericanoMatches = Array.isArray(merged.roundAmericanoMatches) ? merged.roundAmericanoMatches : [] as any;
          merged.isActive = Boolean(merged.isActive);
          merged.isRoundActive = Boolean(merged.isRoundActive);
          merged.isFinished = Boolean(merged.isFinished);
          
          // Convert joinedAt strings back to Date objects for Mexicano players
          merged.players = merged.players.map(p => ({
            ...p,
            joinedAt: p.joinedAt ? (typeof p.joinedAt === 'string' ? new Date(p.joinedAt) : p.joinedAt) : undefined
          }));
          
          // Convert joinedAt strings back to Date objects for Americano players
          merged.americanoPlayers = merged.americanoPlayers.map(p => ({
            ...p,
            joinedAt: p.joinedAt ? (typeof p.joinedAt === 'string' ? new Date(p.joinedAt) : p.joinedAt) : undefined
          }));
          
          // coerce tournamentType
          const tt = merged.tournamentType;
          merged.tournamentType = tt === 'mexicano' || tt === 'americano' || tt === 'team_americano' ? tt : null;
          return merged;
        };
        const parsed: ExtendedTournamentState = migrateState(incoming);
        setTournamentState(parsed);
        setShowLeaderboard(parsed.isFinished ? true : false);
        setCurrentTournamentId(data.tournament_id);
        lastSavedRef.current = JSON.stringify(parsed);
      }
    } catch (e) {
      console.error('Failed to load tournament state', e);
    } finally {
      hasLoaded.current = true;
    }
  }, [user, currentTournamentId, supabase]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    if (!hasLoaded.current) return;
    if (!tournamentState.isActive && tournamentState.currentRound === 0 && !currentTournamentId) return;

    const persist = async () => {
      try {
        let id = currentTournamentId;
        if (!id) {
          id = crypto.randomUUID();
          setCurrentTournamentId(id);
        }
        const isUuid = typeof user.id === 'string' && /^[0-9a-fA-F-]{36}$/.test(user.id);
        if (!isUuid) return;
        const payload: ExtendedTournamentState = { ...tournamentState, stateVersion: tournamentState.stateVersion || schemaVersion };
        const serialized = JSON.stringify(payload);
        if (serialized === lastSavedRef.current) return; // skip identical writes
        addBreadcrumb('tournament', 'persist state', { round: String(tournamentState.currentRound), type: String(tournamentState.tournamentType) });
        const { error } = await supabase.from('tournaments_state').upsert({
          tournament_id: id,
          user_id: user.id,
          state: payload as any,
        }, {
          onConflict: 'tournament_id,user_id'
        });
        if (error) throw error;
        lastSavedRef.current = serialized;
      } catch (e) {
        console.error('Failed to save tournament state', e);
      }
    };
    const handle = setTimeout(persist, 400);
    return () => clearTimeout(handle);
  }, [tournamentState, currentTournamentId, user, supabase]);

  const setTournamentType = (type: 'mexicano' | 'americano' | 'team_americano' | null) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Setting tournament type:', type);
    }
    setTournamentState(prev => ({
      ...prev,
      tournamentType: type
    }));
  };
  
  const startTournament = useMemo(
    () => createStartTournament(setTournamentState, setShowLeaderboard, language, t),
    [language, t]
  );
  const submitRoundResults = useMemo(
    () => createSubmitRoundResults(setTournamentState, t, setShowLeaderboard, supabase, getToken),
    [t, supabase, getToken]
  );
  const finishAmericanoRound = useMemo(
    () => {
      console.log('🔍 Creating finishAmericanoRound with supabase:', !!supabase);
      return createFinishAmericanoRound(setTournamentState, t, supabase, setShowLeaderboard, getToken);
    },
    [t, supabase, setShowLeaderboard, getToken]
  );
  const goToPreviousRound = useMemo(
    () => createGoToPreviousRound(setTournamentState, language),
    [language]
  );
  const finishTournament = () => {
    setTournamentState(prev => ({
      ...prev,
      isActive: false,
      isFinished: true,
      isRoundActive: false,
    }));
    setShowLeaderboard(true);
  };

  // Reset the tournament
  const resetTournament = async (silent?: boolean) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Resetting tournament');
    }
    setTournamentState(defaultTournamentState);
    setShowLeaderboard(false);
    if (user && currentTournamentId) {
      try {
        await supabase
          .from('tournaments_state')
          .delete()
          .eq('user_id', user.id)
          .eq('tournament_id', currentTournamentId);
      } catch (e) {
        console.error('Failed to delete tournament state', e);
      }
    }
    setCurrentTournamentId(null);
    if (!silent) {
      toast.info(t("tournament.mexicano.reset"));
    }
  };

  const contextValue: TournamentContextType = useMemo(() => ({
    tournamentState, 
    startTournament, 
    submitRoundResults, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    finishAmericanoRound,
    finishTournament,
    setTournamentType,
    goToPreviousRound,
    refresh
  }), [
    tournamentState, 
    startTournament, 
    submitRoundResults, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    finishAmericanoRound,
    setTournamentType,
    goToPreviousRound,
    refresh
  ]);
  
  return (
    <TournamentContext.Provider value={contextValue}>
      {children}
    </TournamentContext.Provider>
  );
}
